import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { getDraft } from '../services/intakeApi';
import { rememberDraft } from '../lib/draftStorage';
import { stepPath } from '../steps';
import NotFoundPage from './NotFoundPage';

/**
 * Entry point for a resume link (/r/:token) — from an email, a QR code, or the
 * customer's own bookmark.
 *
 * The token identifies the request but not the shop, so the draft is fetched
 * first to find out where to send them. Once resolved, the token is handed on
 * through router state and storage and the URL is replaced, so it stops being
 * something the customer can paste into a group chat by accident.
 */
const ResumeRoute = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getDraft(token)
      .then((draft) => {
        if (cancelled) return;
        const teamId = draft.organisation?.id;
        if (!teamId) {
          setError('This link is no longer valid');
          return;
        }

        rememberDraft(teamId, draft.quoteRequest.id, token);

        // Land on the photo step: needing to add photos is the overwhelmingly
        // common reason a resume link gets followed.
        navigate(stepPath(teamId, 'photos', draft.quoteRequest.id), {
          replace: true,
          state: { resumeToken: token }
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'This link is no longer valid');
      });

    return () => { cancelled = true; };
  }, [token, navigate]);

  if (error) return <NotFoundPage error={error} />;

  return (
    <div className="min-h-dvh flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
};

export default ResumeRoute;
